import { ListItem, ListItemIcon, ListItemText, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format } from "date-fns";
import pluralize from "pluralize";

import { DotSeparatedText } from "../../Shift/WorkerShift/DotSeparatedText";
import {
  readableVisitTypeMapping,
  shortenedDaysOfWeekMapping,
  VisitPricingTypes,
  VisitTypes,
} from "../constants";
import { type VisitWithCaseData } from "../types";
import {
  getRegularPerHourVisitTimeRangeLabel,
  getVisitWeeklyFrequencyAndDurationLabel,
} from "../utils";

interface VisitDetailsListItemProps {
  visit: VisitWithCaseData;
}

function getVisitDetailsTitle(visit: VisitWithCaseData): string {
  const { type } = visit.attributes;

  if (type !== VisitTypes.REGULAR) {
    return readableVisitTypeMapping[type] ?? type;
  }

  return getVisitWeeklyFrequencyAndDurationLabel(visit);
}

function getVisitDetailsSubtitle(visit: VisitWithCaseData): string {
  const { type, pricingType } = visit.attributes;
  if (type === VisitTypes.REGULAR && pricingType === VisitPricingTypes.PER_HOUR) {
    return getRegularPerHourVisitTimeRangeLabel(visit);
  }

  return "Flexible time";
}

function getVisitDetailsCaptionList(visit: VisitWithCaseData): string[] {
  const { deadline, type, pricingType, daysOfWeek = [] } = visit.attributes;
  const formattedDeadline = isDefined(deadline) ? format(new Date(deadline), "eee, MMM d") : "";
  const subtitleDetails: string[] = [];
  if (type !== VisitTypes.REGULAR) {
    subtitleDetails.push(`by ${formattedDeadline}`);
  } else if (pricingType === VisitPricingTypes.PER_VISIT) {
    subtitleDetails.push(`First visit by ${formattedDeadline}`);
  } else {
    subtitleDetails.push(
      `Start ${formattedDeadline}`,
      `${daysOfWeek.map((day) => shortenedDaysOfWeekMapping[day]).join("/")} ${pluralize(
        "visit",
        daysOfWeek.length
      )}`
    );
  }

  return subtitleDetails;
}

export function VisitDetailsListItem(props: VisitDetailsListItemProps) {
  const { visit } = props;

  return (
    <ListItem divider>
      <ListItemIcon iconType="calendar-clock" />
      <ListItemText
        disableTypography
        primary={
          <Stack>
            <Text semibold variant="h6">
              {getVisitDetailsTitle(visit)}
            </Text>
            <Text semibold variant="h6">
              {getVisitDetailsSubtitle(visit)}
            </Text>
          </Stack>
        }
        secondary={
          <Stack spacing={4} marginTop={2}>
            <DotSeparatedText>
              {getVisitDetailsCaptionList(visit).map((detail) => (
                <Text key={detail} variant="body2">
                  {detail}
                </Text>
              ))}
            </DotSeparatedText>
          </Stack>
        }
      />
    </ListItem>
  );
}
